import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import Tiles from '@cloudscape-design/components/tiles';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';

const CONTENT_TYPES = ['runbook', 'api_doc', 'architecture', 'onboarding', 'compliance', 'security_policy', 'meeting_note', 'project_doc', 'adr'];
const CONTENT_TYPE_OPTIONS = CONTENT_TYPES.map((t) => ({ value: t, label: t.replace('_', ' ') }));
const TIERS = [
  { value: 1, label: 'Tier 1 — Enterprise-Critical', description: 'Zero staleness tolerance, full multi-stage approval' },
  { value: 2, label: 'Tier 2 — Functional-Team', description: '24h-30d staleness, peer + SME review' },
  { value: 3, label: 'Tier 3 — Project/Ephemeral', description: '30-90d staleness, lightweight review' },
  { value: 4, label: 'Tier 4 — Frozen', description: 'Immutable after publish (ADRs, post-incidents)' },
];
const TIER_TILES = TIERS.map((t) => ({ value: String(t.value), label: t.label, description: t.description }));

export function AuthoringPage() {
  const { apiFetch } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [title, setTitle] = useState('');
  const [sourceUri, setSourceUri] = useState('');
  const [content, setContent] = useState('');
  const [contentType, setContentType] = useState('project_doc');
  const [tier, setTier] = useState(2);
  const [tags, setTags] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<'markdown' | 'wysiwyg'>(user?.role === 'technical' ? 'markdown' : 'wysiwyg');

  async function handleSubmit() {
    if (!title || !content) {
      alert('Title and content are required');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editId) {
        await apiFetch(`/documents/${editId}`, {
          method: 'PUT',
          body: { content, content_type: contentType },
        });
        navigate(`/documents/${editId}`);
      } else {
        const result = await apiFetch<{ document_id: string }>('/documents', {
          method: 'POST',
          body: {
            title,
            source_uri: sourceUri || `manamind://manual/${Date.now()}`,
            content,
            content_type: contentType,
            tier,
            tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
          },
        });
        navigate(`/documents/${result.document_id}`);
      }
    } catch (err) {
      alert(`Failed to ${editId ? 'update' : 'create'} document`);
    } finally {
      setIsSubmitting(false);
    }
  }

  const selectedContentType =
    CONTENT_TYPE_OPTIONS.find((o) => o.value === contentType) ?? CONTENT_TYPE_OPTIONS[0];

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        header={<Header variant="h1">{editId ? 'Edit Document' : 'Create Document'}</Header>}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting || !title || !content}
            >
              {isSubmitting ? 'Submitting...' : editId ? 'Update & Re-ingest' : 'Submit for Review'}
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Metadata</Header>}>
            <SpaceBetween size="l">
              <FormField label="Title">
                <Input
                  value={title}
                  onChange={({ detail }) => setTitle(detail.value)}
                  placeholder="Document title"
                />
              </FormField>

              {!editId && (
                <FormField label="Source URI">
                  <Input
                    value={sourceUri}
                    onChange={({ detail }) => setSourceUri(detail.value)}
                    placeholder="https://... or leave blank for auto"
                  />
                </FormField>
              )}

              <FormField label="Content Type">
                <Select
                  selectedOption={selectedContentType}
                  options={CONTENT_TYPE_OPTIONS}
                  onChange={({ detail }) => setContentType(detail.selectedOption.value ?? contentType)}
                />
              </FormField>

              <FormField label="Tier">
                <Tiles
                  columns={2}
                  value={String(tier)}
                  items={TIER_TILES}
                  onChange={({ detail }) => setTier(Number(detail.value))}
                />
              </FormField>

              <FormField label="Tags (controlled vocabulary, comma-separated)">
                <Input
                  value={tags}
                  onChange={({ detail }) => setTags(detail.value)}
                  placeholder="security, aws, compliance"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container
            header={
              <Header
                variant="h2"
                actions={
                  <SegmentedControl
                    label="Editor"
                    selectedId={mode}
                    options={[
                      { id: 'markdown', text: 'Markdown' },
                      { id: 'wysiwyg', text: 'Rich Text' },
                    ]}
                    onChange={({ detail }) => setMode(detail.selectedId as 'markdown' | 'wysiwyg')}
                  />
                }
              >
                Content
              </Header>
            }
          >
            {mode === 'markdown' ? (
              <Textarea
                rows={18}
                value={content}
                onChange={({ detail }) => setContent(detail.value)}
                placeholder={'# Your document content here\n\nWrite in markdown...'}
              />
            ) : (
              <SpaceBetween size="s">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button disabled>B</Button>
                  <Button disabled>I</Button>
                  <Button disabled>H1</Button>
                  <Button disabled>H2</Button>
                  <Button disabled>• List</Button>
                </SpaceBetween>
                <Textarea
                  rows={16}
                  value={content}
                  onChange={({ detail }) => setContent(detail.value)}
                  placeholder="Start writing your document..."
                />
                <Box variant="small" color="text-status-inactive">
                  TipTap WYSIWYG editor would be integrated here in production
                </Box>
              </SpaceBetween>
            )}
          </Container>
        </SpaceBetween>
      </Form>
    </form>
  );
}
